import React, { useEffect } from 'react';

import { useLocation, useNavigate, useParams } from 'react-router-dom';

import Box from '@amzn/meridian/box';
import Divider from '@amzn/meridian/divider';
import Row from '@amzn/meridian/row';
import ConfirmModal from 'src/components/shared/confirmModal/confirmModal';
import LoadingSpinnerWrapper from 'src/components/shared/loadingSpinner/loadingSpinnerWrapper';
import { TOAST_TIMEOUT } from 'src/config/Toast';
import { PLACEMENT_DETAILS_BASEURL } from 'src/constants/Routes';
import useOnDone from 'src/hooks/useOnDone';
import {
  fetchAustinCases,
  getEmployeeInfo,
  getPlacementDetails,
  resetBackgroundFormData,
  resetJmrFilter,
  resetTldAlertModal,
  resetTldAlertModalAcknowledged,
  resetTldWarningModalAcknowledged,
  setAccommodationSavingStatus,
  setApprovalFormData,
  setBackgroundFormData,
  setConsolidatedRegionTasks,
  setCreatedAccommodationId,
  setCurrentPlacementJobShifts,
  setIsCancelModalOpen,
  setJmrFlowType,
  setJmrResults,
  setPageProgress,
  setPlacementId,
  setRestrictionListItems,
  setSelectedLimitations,
  setSelectedRestrictions,
  setSelectedTasks,
  setT2Mode,
} from 'src/store/newJmrPageSlice/newJmrPageSlice';
import { getAllowedSites } from 'src/store/siteSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { createToast } from 'src/store/toastsSlice';
import { ApiCallStatus } from 'src/store/type';
import { CaseSources, formDataFromAustinCase, JmrFlowTypes, JmrPageProgress } from 'src/utils/jmrUtils';

import './newJMR.scss';

import BackgroundPageAUSTIN from './backgroundPage/backgroundPageAUSTIN';
import ProgressTracker from './progressTracker/progressTracker';
import RestrictionsPage from './restrictionsPage/restrictionsPage';
import ResultsPage from './resultsPage/resultsPage';
import ReviewPage from './reviewPage/reviewPage';
import SendForApprovalPage from './sendForApprovalPage/sendForApprovalPage';

// **** Components **** //

/**
 * Determine whether to show the old or the new JMR experience.
 */
const NewJMR = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { caseId, login, type, placementId: placementIdParam, updateType } = useParams();
  const { isLoading } = useAppSelector((state) => state.user);
  const { allowedSites } = useAppSelector((state) => state.sites);
  const {
    austinCases,
    areAustinCasesFetched,
    backgroundFormData,
    jmrFlowType,
    createdAccommodationId,
    pageProgress,
    isCancelModalOpen,
    isLoadingGetPlacementDetails,
    isLoadingPAPI,
  } = useAppSelector((state) => state.newJmrPage);
  const placementId: number | undefined = !!placementIdParam ? Number(placementIdParam) : undefined;
  // Where to send the user after cancel or a successful update: the page they launched from.
  const { returnTo } = (location.state ?? {}) as { returnTo?: string };

  // "providerPlacedOffWork" will be handled by "providerPlacedOffWorkModal" modal
  useEffect(() => {
    if (createdAccommodationId > -1 && jmrFlowType != JmrFlowTypes.ProviderPlacedOffWork) {
      // Finish sends the user where they launched from but replaces history so Back can't re-enter the saved flow
      if (jmrFlowType === JmrFlowTypes.CreateNewAccommodation) {
        navigate(`${PLACEMENT_DETAILS_BASEURL}/${createdAccommodationId}`, { replace: true });
      } else if (returnTo) {
        navigate(returnTo, { replace: true });
      } else {
        navigate(`${PLACEMENT_DETAILS_BASEURL}/${placementId}`, { replace: true });
      }
      dispatch(setAccommodationSavingStatus(ApiCallStatus.Idle));
    }
  }, [createdAccommodationId]);

  // Set on the load/unload behavior
  useEffect(() => {
    // Ensure the latest site configurations are available without having to refresh the browser
    dispatch(getAllowedSites());
    if (placementId !== undefined) {
      dispatch(getPlacementDetails(placementId));
      dispatch(setPlacementId(placementId));
      dispatch(setJmrFlowType(updateType));
    } else {
      dispatch(setJmrFlowType(JmrFlowTypes.CreateNewAccommodation));
    }
    // Return
    return () => {
      dispatch(setPageProgress(JmrPageProgress.Background));
      dispatch(setSelectedTasks([]));
      dispatch(setJmrResults([]));
      dispatch(setCurrentPlacementJobShifts([]));
      // TODO background form data is now reset upon successful accommodation create/update. We should also move this
      // reset logic into the Cancel modal and other relevant locations to align with this state being managed by
      // business logic rather than this component's lifecycle (display logic)
      dispatch(resetBackgroundFormData());
      dispatch(setCreatedAccommodationId(-1));
      dispatch(setApprovalFormData({}));
      dispatch(setJmrFlowType(JmrFlowTypes.CreateNewAccommodation));
      dispatch(resetTldAlertModal());
      dispatch(resetTldAlertModalAcknowledged());
      dispatch(resetTldWarningModalAcknowledged());
      dispatch(setConsolidatedRegionTasks([]));
      dispatch(setRestrictionListItems([]));
      dispatch(setSelectedRestrictions([]));
      dispatch(setSelectedLimitations([]));
      dispatch(resetJmrFilter());
      dispatch(setT2Mode(null));
    };
  }, []);

  // If the user selected "Confirm" in the "Alternative Placement Modal" (the site decision
  // maker accepted an alternative placement) navigate to the "Job Matches" page.
  useEffect(() => {
    if (jmrFlowType === JmrFlowTypes.AlternativePlacementRecommended) {
      dispatch(setPageProgress(JmrPageProgress.JobMatches));
    }
  }, [jmrFlowType]);

  // Prefill data when access /newJMR/:type/:login/:caseId. This route is redirected
  // to if a link in Austin redirects to the placementDetails page, but no
  // placement exists. PlacementDatils routes here so the user is prompted to create a new placement.

  // pre-fill alias and auto-search/fill employee info from PAPI
  useEffect(() => {
    if (!!login) {
      dispatch(setBackgroundFormData({ alias: login }));
      dispatch(getEmployeeInfo(login));
    }
  }, [login]);

  // TODO setting caseSource based on type param carries a minor risk of privilege escelation because the user's
  // permissions are being checked in the useEffect of the child CaseSourceOption component which runs prior to the
  // execution of this useEffect (bottom up). This means we could theoretically set the case source to one the user is not supposed to have access to. Ideally CaseSourceOption should be refactored to separate the business logic from the display logic
  useEffect(() => {
    if (!!type) {
      if (Object.values(CaseSources).includes(type as CaseSources)) {
        dispatch(setBackgroundFormData({ caseSource: type }));
      } else {
        dispatch(
          createToast({
            type: 'error',
            message: `Invalid case source: ${type}`,
            timeout: TOAST_TIMEOUT,
          }),
        );
      }
    }
  }, [type]);

  useEffect(() => {
    if (type === CaseSources.Austin && !!login) {
      dispatch(fetchAustinCases({ alias: login }));
    }
  }, [type, login]);

  useEffect(() => {
    if (!!caseId) {
      if (type !== CaseSources.Austin) {
        // pre-fill Dali/Bali case text input
        dispatch(setBackgroundFormData({ caseID: caseId }));
      } else if (
        !isLoadingPAPI && // getEmployeeInfo overwrites backgroundFormData.siteLocation, wait for it to finish
        areAustinCasesFetched // wait until finished loading Austin cases for AA
      ) {
        // search cases, validate permissions, and pre-fill site and caseId selections
        const formData = formDataFromAustinCase(caseId, austinCases, allowedSites, dispatch);
        dispatch(setBackgroundFormData(formData));
      }
    }
  }, [caseId, type, isLoadingPAPI, areAustinCasesFetched]);

  // Fetch the "Austin Cases (incidents)". Note this is triggered by the ".getPlacementDetails"
  // function call above, so will only tigger if "placementId" in the URL is truthy.
  useOnDone(() => {
    const { caseIdValid } = backgroundFormData;
    if (backgroundFormData.caseSource === CaseSources.Austin && caseIdValid && !!placementId) {
      dispatch(fetchAustinCases({ placementId }));
    }
    // Reset caseID if invalid to force user to pick one.
    if (!caseIdValid) {
      dispatch(
        setBackgroundFormData({
          ...backgroundFormData,
          caseID: '',
        }),
      );
    }
  }, isLoadingGetPlacementDetails);

  return (
    <div className="new-jmr__page-wrapper">
      <LoadingSpinnerWrapper showSpinner={isLoading}>
        <span style={{ visibility: isLoading ? 'hidden' : 'visible' }}>
          {/* Main Content */}
          <Row spacingInset="400 none" alignmentHorizontal="center">
            <Box width="fit-content">
              <ProgressTracker />
            </Box>
          </Row>
          <Divider spacingAfter="400" />

          {/* Show the Different JMR pages based on progress */}
          {pageProgress === JmrPageProgress.Background ? (
            <BackgroundPageAUSTIN />
          ) : pageProgress === JmrPageProgress.Restrictions ? (
            <RestrictionsPage />
          ) : pageProgress === JmrPageProgress.JobMatches ? (
            <ResultsPage />
          ) : pageProgress === JmrPageProgress.Review ? (
            <ReviewPage />
          ) : pageProgress === JmrPageProgress.SendForApproval ? (
            <SendForApprovalPage />
          ) : null}
        </span>
      </LoadingSpinnerWrapper>

      {/* Modals */}
      <ConfirmModal
        width="419px"
        modalTitle="Leave page"
        modalDescription="Leaving this page will restart your Job Match Results process."
        open={isCancelModalOpen}
        setOpen={() => dispatch(setIsCancelModalOpen(false))}
        onConfirm={() => {
          dispatch(setSelectedRestrictions([]));
          dispatch(setSelectedLimitations([]));
          dispatch(setRestrictionListItems([]));
          if (returnTo) {
            navigate(returnTo);
            return;
          }
          // No launch origin (e.g. deep link) then fall back to the placement or overview for the create flow
          navigate(placementId !== undefined ? `${PLACEMENT_DETAILS_BASEURL}/${placementId}` : '/placementsOverview');
        }}
      />
    </div>
  );
};

// **** Export **** //

export default NewJMR;
